<script setup>
import { onBeforeUnmount, reactive, watch } from 'vue'
import { ArrowPathIcon, DocumentTextIcon, PrinterIcon, TableCellsIcon } from '@heroicons/vue/24/outline'
import { useI18n } from '../../i18n.js'
import { session } from '../../session.js'
import { createHistoryExport } from '../../pts2/historyExport.js'
import '../../pts2/historyExport.css'

const props = defineProps({ journal: { type: String, required: true }, filters: { type: Object, required: true },
  scope: { type: String, required: true }, disabled: Boolean })
const { t } = useI18n()
const state = reactive({ busy: false, error: null, format: null })
const actions = [{ format: 'csv', icon: DocumentTextIcon, label: 'pts2ExportCsv', mark: 'CSV' },
  { format: 'xlsx', icon: TableCellsIcon, label: 'pts2ExportXlsx', mark: 'XLSX' },
  { format: 'html', icon: PrinterIcon, label: 'pts2ExportPrint', mark: '' }]
const urls = new Map()
function release(url) { clearTimeout(urls.get(url)); URL.revokeObjectURL(url); urls.delete(url) }
const exporter = createHistoryExport({ onState: (value) => Object.assign(state, value), deliver({ blob, filename, format, popup }) {
  const url = URL.createObjectURL(blob)
  urls.set(url, setTimeout(() => release(url), 60000))
  if (format === 'html') {
    if (!popup || popup.closed) { release(url); throw new Error('Print window closed') }
    popup.location.replace(url)
  } else {
    const link = document.createElement('a'); link.href = url; link.download = filename
    document.body.append(link); link.click(); link.remove()
  }
} })
watch(() => JSON.stringify([props.scope, props.filters, props.journal, session.user?.role, session.user?.effectivePermissions]), () => exporter.cancel(), { flush: 'sync' })
function start(format) {
  if (state.busy || props.disabled) return
  let popup = null
  if (format === 'html') {
    popup = window.open('', '_blank')
    if (!popup) { Object.assign(state, { error: { code: 'pts2_export_popup' }, format }); return }
    popup.opener = null
    popup.document.title = t('pts2ExportPreparing')
    popup.document.body.textContent = t('pts2ExportPreparing')
  }
  exporter.start(props.journal, format, props.filters, popup)
}
function errorText() {
  return t(({ pts2_export_empty: 'pts2ExportEmpty', pts2_export_busy: 'pts2ExportBusy', pts2_export_popup: 'pts2ExportPopup' })[state.error?.code] || 'pts2ExportFailed')
}
onBeforeUnmount(() => { exporter.dispose(); for (const url of urls.keys()) release(url) })
</script>

<template>
  <div class="pts2-export-controls">
    <div class="pts2-export-buttons" role="group" :aria-label="t('pts2ExportActions')" :aria-busy="state.busy">
      <button v-for="action in actions" :key="action.format" type="button" class="pts2-export-button" :disabled="disabled || state.busy"
        :aria-label="t(action.label)" :title="t(action.label)" @click="start(action.format)">
        <ArrowPathIcon v-if="state.busy && state.format === action.format" class="spinning" aria-hidden="true" />
        <template v-else><component :is="action.icon" aria-hidden="true" /><span v-if="action.mark" class="pts2-export-mark" aria-hidden="true">{{ action.mark }}</span></template>
      </button>
    </div>
    <slot />
  </div>
  <p v-if="state.busy" class="pts2-export-feedback" role="status">{{ t('pts2ExportPreparing') }}</p>
  <div v-else-if="state.error" class="pts2-export-feedback pts2-export-error" role="alert"><span>{{ errorText() }}</span>
    <button type="button" :disabled="disabled" @click="start(state.format)">{{ t('pts2ExportRetry') }}</button>
  </div>
</template>
